<template>
    <div id="banned-visitors">
        <AppBreadCrumb :paths="[{ title: $t('message.bannedVisitors') }]" :select="true">
            <a-input-search
                v-model="searchText"
                :placeholder="$t('message.startFilter')"
                class="search-input"
                @search="onSearch"
            />
        </AppBreadCrumb>

        <b-container fluid>
            <a-table
                :columns="cols()"
                :dataSource="getFilteredRows()"
                :loading="isBusy"
                :locale="{
                    emptyText: $t('message.noBannedVisitorFound'),
                }"
                :pagination="false"
                class="custom-table2"
                rowKey="_id"
                @change="tableChange"
            >
                <div :slot="'connectionTimeSlot'" slot-scope="data, item">
                    {{ item.connectionTime | date }}
                </div>
                <div :key="'actionsSlot' + $i18n.locale" :slot="'actionsSlot'" slot-scope="data, item">
                    <div class="d-flex w-100 justify-content-start">
                        <a-button
                            v-if="isRoot || user.role.permissions.map((x) => x.name).includes('visitor.unblock')"
                            v-tippy="{
                                followCursor: true,
                                placement: 'bottom-end',
                                theme: isDarkMode ? 'light-border' : 'google',
                            }"
                            :content="$t('message.removeBanDescription')"
                            class="tableActionButton"
                            shape="circle"
                            size="small"
                            type="primary"
                            @click="unban(item._id)"
                        >
                            <font-awesome-icon icon="unlock-alt" size="sm" />
                        </a-button>
                    </div>
                </div>
            </a-table>
            <div class="mt-3 d-flex justify-content-end">
                <Pagination :page-change="pageChange" :page-sizes="pageSizes" :total="bannedVisitors.total" />
            </div>
        </b-container>

        <div class="mt-3 d-flex justify-content-between">
            <ChatBar v-if="!isAdmin" :visible="false" />
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faUnlockAlt } from "@fortawesome/free-solid-svg-icons";
import AppBreadCrumb from "../../components/common/AppBreadCrumb";
import * as _ from "lodash";
import { log2 } from "../../utils/log2";
import ChatBar from "../../components/ChatBar";
import { sendSocketMessage } from "../../utils/socketStream";
import { tableSorter } from "../../utils/utils";
import Pagination from "../../components/Pagination";

library.add(faUnlockAlt);
const log = log2("BannedVisitors");
export default {
    name: "BannedVisitors",
    components: {
        AppBreadCrumb,
        ChatBar,
        Pagination,
    },
    data() {
        return {
            isBusy: true,
            pageSizes: ["10", "25", "50", "100"],
            searchText: undefined,
            cols: () => [
                {
                    dataIndex: "email",
                    title: this.$t("message.email"),
                    ...tableSorter("email", "string", this.currentSort),
                },
                {
                    dataIndex: "fullname",
                    title: this.$t("message.fullname"),
                    ...tableSorter("fullname", "string", this.currentSort),
                },
                {
                    dataIndex: "country",
                    title: this.$t("message.country"),
                    ...tableSorter("country", "string", this.currentSort),
                },
                {
                    dataIndex: "city",
                    title: this.$t("message.city"),
                },
                {
                    dataIndex: "browser",
                    title: this.$t("message.browser"),
                    ...tableSorter("browser", "string", this.currentSort),
                },
                {
                    dataIndex: "os",
                    title: this.$t("message.os"),
                    ...tableSorter("os", "string", this.currentSort),
                    width: "20%",
                },
                {
                    dataIndex: "connectionTime",
                    title: this.$t("message.connectionDate"),
                    scopedSlots: { customRender: "connectionTimeSlot" },
                    ...tableSorter("connectionTime", "date", this.currentSort),
                    width: "20%",
                },
                {
                    title: this.$t("message.transactions"),
                    scopedSlots: { customRender: "actionsSlot" },
                    width: "10%",
                },
            ],
            page: 1,
            perPage: 100,
            renderCount: 0,
            currentSort: {
                columnKey: "connectionTime",
                order: "ascend",
            },
        };
    },
    computed: {
        ...mapState({
            bannedVisitors: (state) => state.visitor.bannedVisitors,
            site: (state) => state.site.site,
            isAdmin: (state) => state.auth.adminUser != null,
            user: (state) => state.auth.user,
            isRoot: (state) => state.auth.user.role.isRoot,
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),
    },
    methods: {
        ...mapActions({
            getBannedVisitors: "visitor/getBannedvisitors",
            unbanVisitor: "visitor/unbanVisitor",
        }),
        tableChange(pagination, filters, sorter) {
            log("tableChange", { pagination, filters, sorter });
            this.perPage = pagination.pageSize;
            log("tableChange", { pagination, filters, sorter });
            const newSort = {
                columnKey: sorter.columnKey,
                order: sorter.order,
            };
            const oldSort = this.currentSort;
            log("show me current Sort", {
                currentSort: this.currentSort,
                newSort,
            });
            if (!_.isEqual(newSort, oldSort)) {
                this.currentSort = newSort;
                this.renderCount += 1;
                this.$forceUpdate();
            }
        },
        getFilteredRows() {
            return this.searchText
                ? this.bannedVisitors.data.filter(
                      (x) =>
                          x.browser.toLocaleLowerCase("tr-TR").includes(this.searchText.toLocaleLowerCase("tr-TR")) ||
                          x.city.toLocaleLowerCase("tr-TR").includes(this.searchText.toLocaleLowerCase("tr-TR")) ||
                          x.country.toLocaleLowerCase("tr-TR").includes(this.searchText.toLocaleLowerCase("tr-TR")) ||
                          x.os.toLocaleLowerCase("tr-TR").includes(this.searchText.toLocaleLowerCase("tr-TR")) ||
                          x.ipAddress.toLocaleLowerCase("tr-TR").includes(this.searchText.toLocaleLowerCase("tr-TR"))
                  )
                : this.bannedVisitors.data;
        },
        onSearch(value) {
            log("onSearch", value);
            this.searchText = value;
        },
        pageChange(page, pageSize) {
            log("pageChange", { page, pageSize });
            this.page = page;
            this.perPage = pageSize;
        },
        onShowSizeChange(current, pageSize) {
            log("onShowSizeChange", current, pageSize);
            this.page = current;
            this.perPage = pageSize;
        },
        unban(id) {
            this.unbanVisitor(id).then((response) => {
                log("unbanVisitor response", response);
                const socketMessageId = this.isAdmin ? "admin visitor_unblock" : "user visitor-unblock";
                sendSocketMessage(socketMessageId, {
                    siteId: this.site._id,
                    visitorId: id,
                });
                this.$toasted.show(this.$t("message.banRemoved"), {
                    duration: 3000,
                    position: "bottom-center",
                });
            });
        },
        get() {
            this.getBannedVisitors({
                perPage: this.perPage,
                page: this.page,
            }).then(() => (this.isBusy = false));
        },
    },
    mounted() {
        this.get();
    },
    watch: {
        page(newValue) {
            if (newValue) {
                this.get();
            }
        },
        perPage(newValue, oldValue) {
            if (newValue) {
                this.get();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.new-theme {
    #banned-visitors {
        .actionButton {
            border: none;
            box-shadow: none;
        }
    }
}
</style>
